import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useTable,
  useRowSelect,
  useExpanded,
  useSortBy,
  usePagination,
} from "react-table";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "tailwindcss/tailwind.css";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import {
  FaEnvelope,
  FaRegAddressCard,
  FaDownload,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaImage,
  // faSync
} from "react-icons/fa";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { PDFDocument } from "pdf-lib";
import ClipLoader from "react-spinners/ClipLoader";
import api from "../../utils/api"; // Import the API utility
import log from "../../utils/logger"; // Import the logger
import { getAdjustedEventDate } from "../../utils/dateConversion";
import { DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, Space } from "antd";

GlobalWorkerOptions.workerSrc = pdfWorker;

const TableView = ({
  cards,
  selectedCards,
  setSelectedCards,
  setSelectedCustomers,
  onChangeStatus,
  stateMap,
  totalPagesData,
  pageData,
  setFilterEventDate,
  totalDataCount,
  onPageChange,
  // pageSize,
  // onPageSizeChange,
  // handleBatchEnvelopeClick,
  // handleBatchCardsClick,
  handleRefresh,
}) => {
  console.log(
    "totalPagesData: in table",
    totalPagesData,
    "totalDataCount in table:",
    totalDataCount
  );
  const [expandedRows, setExpandedRows] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [batchPreviewUrls, setBatchPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSingleCardModal, setShowSingleCardModal] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [modalType, setModalType] = useState("");
  const initialSortBy = JSON.parse(localStorage.getItem("tableSortBy")) || [];

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const handleRowSelection = useCallback(
    (row) => {
      const cardId = row.original.id;
      const cusId = row.original.customerId;
      setSelectedCards((prevSelectedCards) =>
        prevSelectedCards.includes(cardId)
          ? prevSelectedCards.filter((id) => id !== cardId)
          : [...prevSelectedCards, cardId]
      );
      setSelectedCustomers((prevSelectedCustomer) => {
        // if (prevSelectedCustomer.includes(cusId)) {
        //   return prevSelectedCustomer.filter((id) => id !== cusId);
        // }
        return [...prevSelectedCustomer, cusId];
      });
    },
    [setSelectedCards]
  );

  const handleExpandRow = useCallback((row) => {
    setExpandedRows((prev) => ({
      ...prev,
      [row.id]: !prev[row.id],
    }));
  }, []);

  const handleCardClick = useCallback(async (card) => {
    if (card.cardurl) {
      log.info("Using existing card URL:", card.cardurl);
      setPreviewUrl(card.cardurl);
      setShowSingleCardModal(true);
      return;
    }

    const requestBody = {
      greeting: card.textTo,
      body: card.body,
      signoff: card.textFrom,
      thumbnail: card.cardThumbnail,
    };

    setLoading(true);

    try {
      const response = await api.post("/admin/generate-card", requestBody);
      log.info("API response:", response.data);

      if (response.data.code === 200) {
        const generatedUrl = response.data.data.url;
        log.info("Generated card URL:", generatedUrl);
        setPreviewUrl(generatedUrl);
        setShowSingleCardModal(true);
      } else {
        log.error("Failed to generate card preview:", response.data.message);
      }
    } catch (error) {
      log.error("Failed to generate card preview:", error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleEnvelopeClick = useCallback(async (card) => {
    const requestBody = {
      return_address: {
        name: "The Johnsons",
        address: ["123 NORTH MAIN STREET", "SMALLTOWN, MAINE 12345"],
      },
      addresses: [
        {
          Name: card.recipientName,
          Street: card.address.split(",")[0].trim(),
          City: card.address.split(",")[1].trim(),
          State: card.address.split(",")[2].trim(),
          ZIP: card.address.split(",")[3].trim(),
        },
      ],
    };

    setLoading(true);

    try {
      const response = await api.post("/admin/generate-envelope", requestBody);
      log.info("API response:", response.data);

      if (response.data.code === 200) {
        const generatedUrl = response.data.data.s3_links[0];
        log.info("Generated envelope URL:", generatedUrl);
        setPreviewUrl(generatedUrl);
        setShowSingleCardModal(true);
      } else {
        log.error(
          "Failed to generate envelope preview:",
          response.data.message
        );
      }
    } catch (error) {
      log.error("Failed to generate envelope preview:", error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRemoveBatchPreview = useCallback(
    (index) => {
      const newBatchPreviewUrls = [...batchPreviewUrls];
      newBatchPreviewUrls.splice(index, 1);
      setBatchPreviewUrls(newBatchPreviewUrls);
      const cardId = selectedCards[index];
      setSelectedCards(selectedCards.filter((id) => id !== cardId));

      if (newBatchPreviewUrls.length > 0) {
        setPreviewUrl(newBatchPreviewUrls[0]);
      } else {
        setPreviewUrl(null);
      }
    },
    [batchPreviewUrls, selectedCards, setSelectedCards]
  );

  const generateThumbnail = useCallback(async (url, index) => {
    try {
      const loadingTask = getDocument(url);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1 });

      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext("2d");
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      const thumbnailUrl = canvas.toDataURL();
      setThumbnailUrls((prev) => ({ ...prev, [index]: thumbnailUrl }));
    } catch (error) {
      log.error("Failed to generate thumbnail:", error.message);
    }
  }, []);

  useEffect(() => {
    batchPreviewUrls.forEach((url, index) => {
      if (!thumbnailUrls[index]) {
        generateThumbnail(url, index);
      }
    });
  }, [batchPreviewUrls, thumbnailUrls, generateThumbnail]);

  const generateFacePdf = useCallback(
    async (pageIndex) => {
      setLoading(true);
      const mergedPdf = await PDFDocument.create();
      try {
        for (const url of batchPreviewUrls) {
          const pdfBytes = await fetch(url).then((res) => res.arrayBuffer());
          const pdf = await PDFDocument.load(pdfBytes);
          if (pdf.getPageCount() > pageIndex) {
            const copiedPages = await mergedPdf.copyPages(pdf, [pageIndex]);
            copiedPages.forEach((page) => mergedPdf.addPage(page));
          }
        }
        const mergedPdfBytes = await mergedPdf.save();
        const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(blob);
        setPreviewUrl(pdfUrl);
      } catch (error) {
        log.error("Failed to generate face PDF:", error.message);
      } finally {
        setLoading(false);
      }
    },
    [batchPreviewUrls]
  );

  const handleDownloadCompleteCards = useCallback(async () => {
    const zip = new JSZip();
    try {
      for (const url of batchPreviewUrls) {
        const pdfBytes = await fetch(url).then((res) => res.arrayBuffer());
        zip.file(`card_${batchPreviewUrls.indexOf(url) + 1}.pdf`, pdfBytes);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "cards.zip");
    } catch (error) {
      log.error("Failed to download complete cards:", error.message);
    }
  }, [batchPreviewUrls]);

  const resetStates = useCallback(() => {
    setPreviewUrl(null);
    setBatchPreviewUrls([]);
    setShowBatchModal(false);
    setShowSingleCardModal(false);
    setThumbnailUrls({});
    setModalType("");
  }, []);

  const data = useMemo(
    () =>
      cards.map((card) => ({
        id: card._id,
        customerId: card.customerId._id,
        finalEventDate: getAdjustedEventDate(card.eventDate, card)
          .toISOString()
          .split("T")[0],
        printByDate: new Date(
          getAdjustedEventDate(card.eventDate, card).setDate(
            new Date(getAdjustedEventDate(card.eventDate, card)).getDate() - 6
          )
        )
          .toISOString()
          .split("T")[0],
        locked: card.isLocked ? "Yes" : "No",
        recipientName: card.receiptsId.fullName,
        userName: card.customerId.fullName,
        eventName: card.eventId.name,
        email: card.receiptsId.email,
        streetAddress: card.receiptsId.streetAddress || "",
        city: card.receiptsId.city || "",
        state: stateMap[card.receiptsId.stateId] || card.receiptsId.stateId,
        zipCode: card.receiptsId.zipCode || "",
        subscriptionStatus: card.customerId.isSubscribed
          ? "Subscribed"
          : "Not Subscribed",
        address: `${card.receiptsId.streetAddress || ""}, ${
          card.receiptsId.city || ""
        }, ${
          stateMap[card.receiptsId.stateId] || card.receiptsId.stateId || ""
        }, ${card.receiptsId.zipCode || ""}`,
        recipientGender: card.receiptsId.gender,
        cardThumbnail: card.cardId?.thumbnail || "",
        status: card.status,
        textTo: card.textTo,
        textFrom: card.textFrom,
        body: card.textCard,
        message: `${card.textTo}\n\n${card.textCard}\n\n${card.textFrom}`,
        eventDate: new Date(card.eventDate).toLocaleDateString(),
        cardurl: card.cardURL,
      })),
    [cards, stateMap, getAdjustedEventDate]
  );

  const columns = useMemo(
    () => [
      {
        Header: " ",
        accessor: "select",
        disableSortBy: true,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedCards.includes(row.original.id)}
            onChange={() => handleRowSelection(row)}
            className="form-checkbox h-4 w-4 accent-primary transition duration-150 ease-in-out bg-red"
          />
        ),
      },

      {
        Header: "Print By Date",
        accessor: "printByDate",
        Cell: ({ row }) => {
          const today = new Date();
          const printByDate = new Date(row.original.printByDate);
          const isLate = today > printByDate;
          const isToday = today.toDateString() === printByDate.toDateString();

          let color = "";
          // if (row.original.status === "awaiting action") {
          //   if (isLate) color = "bg-red-500 text-white";
          //   else if (isToday) color = "bg-orange-500 text-white";
          // }
          return (
            <span
            // className={` inline-flex text-xs leading-5 font-semibold rounded-full ${color}`}
            >
              {row.original.printByDate}
            </span>
          );
        },
      },
      {
        Header: "Event Date",
        accessor: "finalEventDate",
        id: "EventDate",
      },
      {
        Header: "Street",
        accessor: "streetAddress",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "printed"
                ? "bg-orange-100 text-orange-800"
                : value === "mailed"
                ? "bg-green-100 text-green-800"
                : ""
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "City",
        accessor: "city",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "printed"
                ? "bg-orange-100 text-orange-800"
                : value === "mailed"
                ? "bg-green-100 text-green-800"
                : ""
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "State",
        accessor: "state",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "printed"
                ? "bg-orange-100 text-orange-800"
                : value === "mailed"
                ? "bg-green-100 text-green-800"
                : ""
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Zipcode",
        accessor: "zipCode",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "printed"
                ? "bg-orange-100 text-orange-800"
                : value === "mailed"
                ? "bg-green-100 text-green-800"
                : ""
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Recipient Name",
        accessor: "recipientName",
      },
      {
        Header: "Username",
        accessor: "userName",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "printed"
                ? "bg-orange-100 text-orange-800"
                : value === "mailed"
                ? "bg-green-100 text-green-800"
                : ""
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Occasion",
        accessor: "eventName",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "printed"
                ? "bg-orange-100 text-orange-800"
                : value === "mailed"
                ? "bg-green-100 text-green-800"
                : ""
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Locked",
        accessor: "locked",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "Yes"
                ? "bg-red-100 text-red-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {value}
          </span>
        ),
      },

      {
        Header: "Subscription Status",
        accessor: "subscriptionStatus",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "Subscribed"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Order Status",
        accessor: "status",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "printed"
                ? "bg-orange-100 text-orange-800"
                : value === "mailed"
                ? "bg-green-100 text-green-800"
                : ""
            }`}
          >
            {value === "printed" ? "printed but not mailed" : value}
          </span>
        ),
      },
      {
        Header: "Email Address",
        accessor: "email",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "Subscribed"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {value}
          </span>
        ),
      },

      {
        Header: "Gender",
        accessor: "recipientGender",
        Cell: ({ value }) => (
          <span
            className={` inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === "printed"
                ? "bg-orange-100 text-orange-800"
                : value === "mailed"
                ? "bg-green-100 text-green-800"
                : ""
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => handleCardClick(row.original)}
              className="text-primary hover:text-primary-dark"
            >
              <FaRegAddressCard />
            </button>
            <button
              onClick={() => handleEnvelopeClick(row.original)}
              className="text-blue-600 hover:text-blue-800"
            >
              <FaEnvelope />
            </button>
            <button
              onClick={() => handleExpandRow(row)}
              className="text-primary"
            >
              {expandedRows[row.id] ? <FaChevronUp /> : <FaChevronDown />}
            </button>
          </div>
        ),
      },
    ],
    [
      selectedCards,
      expandedRows,
      handleRowSelection,
      handleCardClick,
      handleEnvelopeClick,
      handleExpandRow,
    ]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    selectedFlatRows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { sortBy, pageIndex, pageSize },
    nextPage,
    previousPage,
    gotoPage,
    setPageSize,
  } = useTable(
    {
      columns,
      data,

      // 👇 prevent jumping back to page 1 on *any* re-render/change
      autoResetPage: false, // v7 option
      autoResetPageIndex: false, // harmless alias; future-proof if you switch libs
      autoResetSortBy: false, // you already had this
      autoResetSelectedRows: false, // keep selections
      autoResetExpanded: false, // keep expanded rows
      autoResetFilters: false, // optional: keeps filters
      autoResetGlobalFilter: false, // optional: keeps global filter

      initialState: {
        sortBy: initialSortBy,
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useSortBy,
    usePagination,
    useRowSelect
  );
  const handleSelectAll = () => {
    const isAllSelected = selectedCards.length === rows.length;
    setSelectedCards(isAllSelected ? [] : rows.map((row) => row.original.id));
  };

  // }, [sortBy])
  const totalPages = Math.ceil(totalPagesData / pageSize);

  // Dynamically calculate available page sizes
  const pageSizeOptions = [10, 20, 30, 40, 50].filter(
    (size) => size < totalPagesData
  );
  pageSizeOptions.push(totalPagesData); // Add "All" option

  // Dropdown menu items
  const menuItems = pageSizeOptions.map((size) => ({
    key: size,
    label: size === totalPagesData ? "All" : `Show ${size}`,
  }));

  const handleMenuClick = (e) => {
    setPageSize(Number(e.key));
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };
  return (
    <React.Fragment>
      <div className="overflow-x-auto">
        {loading && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
            <ClipLoader size={64} color={"#fff"} />
          </div>
        )}

        {showBatchModal && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white p-4 squared-lg w-full max-w-5xl h-full flex flex-col relative z-60">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold">
                  {modalType === "cards"
                    ? "Print Cards Preview"
                    : "Print Envelopes Preview"}
                </h2>
                <button
                  onClick={resetStates}
                  className="bg-primary text-white squared-full p-2"
                >
                  <FaTimes />
                </button>
              </div>
              {loading ? (
                <div className="flex justify-center items-center h-full">
                  <ClipLoader size={64} color={"#000"} />
                </div>
              ) : (
                <>
                  {modalType === "cards" ? (
                    <div className="flex justify-center mb-4">
                      <button
                        onClick={() => generateFacePdf(0)}
                        className="bg-primary hover:bg-primary-dark text-white px-4 py-2 squared-l flex items-center"
                      >
                        <FaImage className="mr-2" /> All Outside Faces
                      </button>
                      <button
                        onClick={() => generateFacePdf(1)}
                        className="bg-primary hover:bg-primary-dark text-white px-4 py-2 squared-r flex items-center"
                      >
                        <FaImage className="mr-2" /> All Inside Faces
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-center mb-4">
                      <button
                        onClick={() => generateFacePdf(0)}
                        className="bg-primary hover:bg-primary-dark text-white px-4 py-2 squared flex items-center"
                      >
                        <FaImage className="mr-2" /> All Envelopes
                      </button>
                    </div>
                  )}
                  <div className="flex h-full overflow-hidden">
                    <div className="w-1/4 overflow-y-auto pr-2 h-full">
                      {batchPreviewUrls.map((url, index) => (
                        <div key={index} className="relative mb-4">
                          <button
                            onClick={() => handleRemoveBatchPreview(index)}
                            className="absolute top-0 left-0 bg-primary text-white squared-full p-1"
                          >
                            <FaTimes />
                          </button>
                          <div
                            className="w-full h-32 border overflow-hidden cursor-pointer"
                            onClick={() => setPreviewUrl(url)}
                          >
                            {thumbnailUrls[index] ? (
                              <img
                                src={thumbnailUrls[index]}
                                alt={`Preview ${index}`}
                                className="w-full h-full object-fill"
                              />
                            ) : (
                              <div>Loading...</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex-grow h-full overflow-y-auto pl-2">
                      {previewUrl && (
                        <Worker workerUrl={pdfWorker}>
                          <Viewer
                            fileUrl={previewUrl}
                            plugins={[defaultLayoutPluginInstance]}
                            renderError={() => <div>Error loading PDF</div>}
                          />
                        </Worker>
                      )}
                    </div>
                  </div>
                  {modalType === "cards" && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={handleDownloadCompleteCards}
                        className="bg-primary hover:bg- text-white px-4 py-2 squared flex items-center"
                      >
                        <FaDownload className="mr-2" /> Download Complete Cards
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        {showSingleCardModal && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white p-4 squared-lg w-full max-w-4xl h-full flex flex-col relative z-60">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold">Card Preview</h2>
                <button
                  onClick={resetStates}
                  className="bg-primary text-white squared-full p-2"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="flex-grow h-full overflow-y-auto">
                {previewUrl && (
                  <Worker workerUrl={pdfWorker}>
                    <Viewer
                      fileUrl={previewUrl}
                      plugins={[defaultLayoutPluginInstance]}
                      renderError={() => <div>Error loading PDF</div>}
                    />
                  </Worker>
                )}
              </div>
            </div>
          </div>
        )}
        <table
          {...getTableProps()}
          className="min-w-full bg-white divide-y divide-gray-100"
        >
          <thead className="bg-gray-100">
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: columnKey, ...columnProps } =
                      column.getHeaderProps(column.getSortByToggleProps());
                    return (
                      <th
                        key={columnKey}
                        {...columnProps}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                      >
                        <div className="flex items-center">
                          {column.render("Header")}
                          {column.Header === " " && (
                            <input
                              type="checkbox"
                              className=" form-checkbox h-4 w-4 accent-primary transition duration-150 ease-in-out"
                              onChange={handleSelectAll}
                              checked={selectedCards.length === rows.length}
                            />
                          )}
                          {column.isSorted && (
                            <FontAwesomeIcon
                              icon={column.isSortedDesc ? faSortDown : faSortUp}
                              className="ml-1"
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className="bg-white divide-y divide-gray-200"
          >
            {page.map((row) => {
              prepareRow(row);
              const { key: rowKey, ...rowProps } = row.getRowProps();
              return (
                <React.Fragment key={rowKey}>
                  <tr key={rowKey} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td
                          key={cellKey}
                          {...cellProps}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-800"
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  {expandedRows[row.id] && (
                    <tr key={`expanded-${rowKey}`}>
                      <td colSpan={columns.length}>
                        <div className="p-4 bg-gray-50">
                          <p>
                            <strong>Message:</strong> {row.original.message}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4 flex-wrap gap-4">
        <Dropdown
          menu={menuProps}
          className="py-2.5 mt-2 w-full sm:w-auto flex-grow sm:flex-none"
        >
          <Button className="border border-gray-200 shadow-none w-full sm:w-auto rounded-md">
            <Space className="py-2.5">
              {pageSize === totalPagesData ? "All" : `Show ${pageSize}`}
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>

        <div className="flex items-center gap-2">
          {/* First */}
          <button
            className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium disabled:bg-gray-300"
            onClick={() => onPageChange(1)}
            disabled={pageData === 1}
          >
            {"<<"}
          </button>

          {/* Previous */}
          <button
            className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium disabled:bg-gray-300"
            onClick={() => onPageChange(pageData - 1)}
            disabled={pageData === 1}
          >
            {"<"}
          </button>

          <span className="text-sm font-medium">
            Page{" "}
            <strong>
              {pageData} of {totalPagesData}
            </strong>
          </span>

          {/* Next */}
          <button
            className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium disabled:bg-gray-300"
            onClick={() => onPageChange(pageData + 1)}
            disabled={pageData === totalPagesData}
          >
            {">"}
          </button>

          {/* Last */}
          <button
            className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium disabled:bg-gray-300"
            onClick={() => onPageChange(totalPagesData)}
            disabled={pageData === totalPagesData}
          >
            {">>"}
          </button>
        </div>
      </div>
    </React.Fragment>
  );
};

export default TableView;
